/**
 * OptionsHubPage — /options — the single, beginner-first home for options.
 *
 * A new UNIFIED layer over the existing (still-reachable) options surfaces:
 *   • Overview — "am I OK?" portfolio roll-up + what needs attention + quick actions.
 *   • Trade    — express a plain-language view → ranked, defined-risk baskets (Architect).
 *   • Monitor  — every placed basket in one place, auto-managed, with plain-English outcomes.
 *   • Learn    — an on-demand glossary of every term used across the options screens.
 *
 * Existing pages (FNO Intelligence, Options Lab, Strategies→Options AT) stay live and
 * are linked from here, so nothing you rely on disappears.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Tabs, Tab, Typography, Card, CardContent, Button, Chip, Grid, Divider,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ScienceIcon from '@mui/icons-material/Science';
import TuneIcon from '@mui/icons-material/Tune';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePersistedState } from '../hooks/usePersistedState';
import { optionStrategyService } from '../services/optionStrategy.service';
import OptionsPortfolioHeader from '../components/options/OptionsPortfolioHeader';
import OptionBasketsMonitor from '../components/options/OptionBasketsMonitor';
import OptionCampaignMonitor from '../components/options/OptionCampaignMonitor';
import AutoEngineSummary from '../components/options/AutoEngineSummary';
import AllPositionsLedger from '../components/options/AllPositionsLedger';
import PortfolioGreeks from '../components/options/PortfolioGreeks';
import StrategyArchitect from '../components/options/StrategyArchitect';
import { OPTIONS_GLOSSARY } from '../utils/optionsGlossary';
import { Term } from '../components/options/shared/OptionsUi';

const TABS = ['overview', 'trade', 'monitor', 'learn'] as const;
type HubTab = typeof TABS[number];

const OptionsHubPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = usePersistedState<HubTab>('optionsHub.tab', 'overview');
  const [watchlists, setWatchlists] = useState<any[]>([]);

  // A ?tab= deep link (e.g. from a "Build a trade" button) selects the tab.
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('tab');
    if (q && (TABS as readonly string[]).includes(q)) setTab(q as HubTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    optionStrategyService.watchlists().then((r: any) => setWatchlists(r.data?.data || [])).catch(() => {});
  }, []);

  const idx = Math.max(0, TABS.indexOf(tab));

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Options</Typography>
        <Chip size="small" label="beginner-friendly" color="success" variant="outlined" sx={{ height: 20, fontWeight: 700 }} />
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="text" startIcon={<InsightsIcon />} onClick={() => navigate('/options-dashboard')}>FNO Intelligence</Button>
        <Button size="small" variant="text" startIcon={<TuneIcon />} onClick={() => navigate('/strategies')}>Auto-trade engines</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        One home for options: see how you’re doing, build a trade from a plain-language view, watch it auto-manage,
        and learn the terms as you go. Hover any <Box component="span" sx={{ borderBottom: '1px dotted', borderColor: 'text.disabled' }}>dotted word</Box> for a plain-English explanation.
      </Typography>

      <Tabs value={idx} onChange={(_, v) => setTab(TABS[v])} sx={{ mb: 2, minHeight: 42, '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontWeight: 700 } }}>
        <Tab icon={<MonitorHeartIcon fontSize="small" />} iconPosition="start" label="Overview" />
        <Tab icon={<AutoFixHighIcon fontSize="small" />} iconPosition="start" label="Trade" />
        <Tab icon={<ShowChartIcon fontSize="small" />} iconPosition="start" label="Monitor" />
        <Tab icon={<MenuBookIcon fontSize="small" />} iconPosition="start" label="Learn" />
      </Tabs>

      {tab === 'overview' && <OverviewTab onTrade={() => setTab('trade')} onMonitor={() => setTab('monitor')} navigate={navigate} />}
      {tab === 'trade' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Pick an underlying, say what you think happens and by when, and the engine builds &amp; ranks
            <Term k="basket" sx={{ mx: 0.5 }}>baskets</Term> for your view. Every result shows its
            <Term k="maxLoss" sx={{ mx: 0.5 }}>max loss</Term> before you place. Start with
            <Term k="paper" sx={{ mx: 0.5 }}>paper trades</Term> — no real money.
          </Typography>
          <StrategyArchitect watchlists={watchlists} defaultSymbol={new URLSearchParams(location.search).get('symbol') || undefined} onExecuted={() => setTab('monitor')} />
        </>
      )}
      {tab === 'monitor' && (
        <>
          <Box sx={{ mb: 2 }}><OptionsPortfolioHeader /></Box>
          {/* One index of EVERY open position — baskets, campaigns and auto-engine cycles. */}
          <Box sx={{ mb: 3 }}><AllPositionsLedger /></Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Manage a position below — adjust, close, or view its payoff. Auto-engine trades are managed on the Auto-trade engines page.
          </Typography>
          <Box id="hub-positions-detail">
            <OptionBasketsMonitor />
            <Box sx={{ mt: 3 }}><OptionCampaignMonitor /></Box>
            <AutoEngineSummary />
          </Box>
        </>
      )}
      {tab === 'learn' && <LearnTab />}
    </Box>
  );
};

// ── Overview ──────────────────────────────────────────────────────────────────
const OverviewTab: React.FC<{ onTrade: () => void; onMonitor: () => void; navigate: (p: string) => void }> = ({ onTrade, onMonitor, navigate }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <OptionsPortfolioHeader onViewAll={onMonitor} />
    <PortfolioGreeks />

    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 4 }}>
        <QuickAction icon={<AutoFixHighIcon color="primary" />} title="Build a trade"
          body="Express a view in plain language and get ranked, defined-risk baskets. Paper-trade first."
          cta="Start building" onClick={onTrade} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <QuickAction icon={<ShowChartIcon color="primary" />} title="Watch your positions"
          body="Every placed basket in one place, auto-managed with take-profit / stop-loss and a plain-English status."
          cta="Open monitor" onClick={onMonitor} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <QuickAction icon={<InsightsIcon color="primary" />} title="Scan the market"
          body="FNO Intelligence: live option-chain signals, IV rank and conviction scores per underlying."
          cta="Open FNO Intelligence" onClick={() => navigate('/options-dashboard')} />
      </Grid>
    </Grid>

    <Card variant="outlined">
      <CardContent>
        <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Where everything lives</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          One map of the whole options area — jump straight to what you need. This hub is the front door; each surface has one job.
        </Typography>
        <Grid container spacing={1.25}>
          {([
            { icon: <AutoFixHighIcon fontSize="small" color="primary" />, title: 'Trade', desc: 'Build a basket from a plain-language view', onClick: onTrade },
            { icon: <MonitorHeartIcon fontSize="small" color="primary" />, title: 'Monitor', desc: 'Every open position in one place', onClick: onMonitor },
            { icon: <TuneIcon fontSize="small" color="primary" />, title: 'Auto-trade engines', desc: '24×7 strategies that enter & exit on their own', onClick: () => navigate('/option-strategies') },
            { icon: <InsightsIcon fontSize="small" color="primary" />, title: 'FNO Intelligence', desc: 'Live chain signals, IV, GEX & conviction', onClick: () => navigate('/options-dashboard') },
            { icon: <ScienceIcon fontSize="small" color="primary" />, title: 'Options Lab', desc: 'Deep research on one underlying (open from FNO Intelligence)', onClick: () => navigate('/options-dashboard') },
            { icon: <ShowChartIcon fontSize="small" color="primary" />, title: 'Charts builder', desc: 'Build & see the payoff on the price chart', onClick: () => navigate('/charts') },
          ]).map(s => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={s.title}>
              <Box onClick={s.onClick} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5, border: theme => `1px solid ${theme.palette.divider}`, cursor: 'pointer', height: '100%', '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' } }}>
                {s.icon}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.82rem' }}>{s.title}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>{s.desc}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>

    <Card variant="outlined">
      <CardContent>
        <Typography sx={{ fontWeight: 800, mb: 0.5 }}>New to options? Read this first.</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          An <Term k="option">option</Term> is a contract that pays off based on where a price finishes by a
          <Term k="expiry" sx={{ mx: 0.5 }}>deadline</Term>. Instead of trading single options, this app trades
          <Term k="basket" sx={{ mx: 0.5 }}>baskets</Term> — safe, pre-packaged combinations whose
          <Term k="maxLoss" sx={{ mx: 0.5 }}>worst case</Term> is known up front. The engine ranks them by a
          <Term k="fitScore" sx={{ mx: 0.5 }}>fit score</Term> for your view, and manages the exit for you.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" startIcon={<MenuBookIcon />} onClick={() => window.open('/app-guide#fno-options', '_blank', 'noopener,noreferrer')}>Full guide</Button>
          <Button size="small" variant="outlined" startIcon={<ScienceIcon />} onClick={onTrade}>Try a paper trade</Button>
        </Box>
      </CardContent>
    </Card>
  </Box>
);

const QuickAction: React.FC<{ icon: React.ReactNode; title: string; body: string; cta: string; onClick: () => void }> = ({ icon, title, body, cta, onClick }) => (
  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <CardContent sx={{ flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>{icon}<Typography sx={{ fontWeight: 800 }}>{title}</Typography></Box>
      <Typography variant="body2" color="text.secondary">{body}</Typography>
    </CardContent>
    <Box sx={{ px: 2, pb: 1.5 }}><Button size="small" variant="contained" onClick={onClick}>{cta}</Button></Box>
  </Card>
);

// ── Learn (glossary) ──────────────────────────────────────────────────────────
const LEARN_GROUPS: Array<{ heading: string; keys: string[] }> = [
  { heading: 'The basics', keys: ['option', 'call', 'put', 'premium', 'strike', 'expiry'] },
  { heading: 'How we trade — baskets', keys: ['basket', 'leg', 'credit', 'debit', 'undefinedRisk', 'straddle', 'ironCondor'] },
  { heading: 'Reading a recommendation', keys: ['fitScore', 'thesis', 'winProbability', 'expectedPnl', 'rewardRisk', 'maxProfit', 'maxLoss', 'breakEven', 'cvar', 'ivRank', 'margin'] },
  { heading: 'The Greeks (how it reacts)', keys: ['greeks', 'delta', 'gamma', 'theta', 'vega'] },
  { heading: 'Managing a live position', keys: ['netToClose', 'targetPremium', 'slPremium', 'paper', 'live'] },
];

const LearnTab: React.FC = () => (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      A plain-English glossary of everything you’ll see on the options screens. These same explanations pop up when you
      hover a dotted term anywhere in the app.
    </Typography>
    {LEARN_GROUPS.map(group => (
      <Card key={group.heading} variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography sx={{ fontWeight: 800, mb: 1 }}>{group.heading}</Typography>
          <Grid container spacing={1.5}>
            {group.keys.map(k => {
              const e = OPTIONS_GLOSSARY[k];
              if (!e) return null;
              return (
                <Grid size={{ xs: 12, sm: 6 }} key={k}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>{e.title}</Typography>
                      <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', fontStyle: 'italic' }}>{e.oneLiner}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>{e.plain}</Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    ))}
    <Divider sx={{ my: 2 }} />
    <Typography variant="caption" color="text.secondary">
      Want more depth? The full <Box component="a" href="/app-guide#fno-options" target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main' }}>App Guide → Options</Box> covers strategy mechanics, the scoring engine and the auto-trade rules.
    </Typography>
  </Box>
);

export default OptionsHubPage;
